import React, { useEffect, useState, useRef } from 'react';
import { authAPI, taskAPI, userAPI } from '../services/api';
import TaskCreate from './TaskCreate';
import TaskList from './TaskList';
import Sidebar from './Sidebar';
import UserList from './UserList';
import AddUser from './AddUser';
import UserData from './UserData';
import FileUpload from './FileUpload';
import TextToSpeech from './TextToSpeech';
import TextTranslate from './TextTranslate';
import Chat from './Chat';
import './Dashboard.css';
import TicTacToe from './TicTacToe';
import Sudoku from './Sudoku';
import VtoS from './videotoshorts';

const Dashboard = ({ onLogout }) => {
  const [tasks, setTasks] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);  // add this in state section
  const [user, setUserInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('my');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  // ...existing code...
  const [showArchived, setShowArchived] = useState(false);
  const [recentlyUpdatedTaskIds, setRecentlyUpdatedTaskIds] = useState([]);
// ...existing code...
  
  // 🔧 FIX: Add state to prevent duplicate API calls
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
  const [isFetchingTasks, setIsFetchingTasks] = useState(false);
  
  // Refs to track notification state and prevent duplicates
  const processedTaskIds = useRef(new Set());
  const lastNotificationCheck = useRef(Date.now());
  const lastTaskCount = useRef(0); // Track task count for real-time updates
  const taskUpdateInterval = useRef(null);
  
  // Audio context for notification sound
  const audioContextRef = useRef(null);
  const notificationSoundEnabled = useRef(true);

  // Define status order for sorting
  const statusOrder = [
    "assigned",
    "not started", 
    "in progress", 
    "team review", 
    "client review", 
    "rejected by team", 
    "rejected by client", 
    "approved", 
    "completed", 
    "posted"
  ];
  

  // Initialize audio context and notification system
  useEffect(() => {
    const initAudioContext = () => {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (error) {
        console.error('Failed to initialize audio context:', error);
      }
    };

    initAudioContext();

    const resumeAudioContext = () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
    };

    document.addEventListener('click', resumeAudioContext);
    document.addEventListener('keydown', resumeAudioContext);
    document.addEventListener('touchstart', resumeAudioContext);

    return () => {
      document.removeEventListener('click', resumeAudioContext);
      document.removeEventListener('keydown', resumeAudioContext);
      document.removeEventListener('touchstart', resumeAudioContext);
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  

  // Enhanced WhatsApp-like notification sound
  const playNotificationSound = async () => {
    if (!notificationSoundEnabled.current || !audioContextRef.current) return;

    try {
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const audioContext = audioContextRef.current;
      const now = audioContext.currentTime;

      const createWhatsAppLikeSound = () => {
        const osc1 = audioContext.createOscillator();
        const gain1 = audioContext.createGain();
        
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(800, now);
        osc1.frequency.exponentialRampToValueAtTime(1000, now + 0.1);
        osc1.frequency.exponentialRampToValueAtTime(800, now + 0.2);
        
        gain1.gain.setValueAtTime(0, now);
        gain1.gain.linearRampToValueAtTime(0.4, now + 0.02);
        gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
        
        osc1.connect(gain1);
        gain1.connect(audioContext.destination);
        
        osc1.start(now);
        osc1.stop(now + 0.25);

        setTimeout(() => {
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          
          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(600, audioContext.currentTime);
          osc2.frequency.exponentialRampToValueAtTime(750, audioContext.currentTime + 0.1);
          osc2.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.2);
          
          gain2.gain.setValueAtTime(0, audioContext.currentTime);
          gain2.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.02);
          gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
          
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          
          osc2.start(audioContext.currentTime);
          osc2.stop(audioContext.currentTime + 0.25);
        }, 150);

        setTimeout(() => {
          const osc3 = audioContext.createOscillator();
          const gain3 = audioContext.createGain();
          
          osc3.type = 'sine';
          osc3.frequency.setValueAtTime(400, audioContext.currentTime);
          osc3.frequency.exponentialRampToValueAtTime(500, audioContext.currentTime + 0.15);
          
          gain3.gain.setValueAtTime(0, audioContext.currentTime);
          gain3.gain.linearRampToValueAtTime(0.25, audioContext.currentTime + 0.02);
          gain3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          
          osc3.connect(gain3);
          gain3.connect(audioContext.destination);
          
          osc3.start(audioContext.currentTime);
          osc3.stop(audioContext.currentTime + 0.2);
        }, 300);
      };

      createWhatsAppLikeSound();

    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Enhanced notification system that works in background
  const showNotificationWithSound = (title, body, taskId) => {
    playNotificationSound();

    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body: body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `task_${taskId}`,
        requireInteraction: true,
        silent: false,
        vibrate: [200, 100, 200],
        data: {
          taskId: taskId,
          timestamp: Date.now()
        }
      });

      notification.onclick = function(event) {
        event.preventDefault();
        window.focus();
        notification.close();
        
        setActiveTab('my');
        setShowNotifications(true);
      };

      setTimeout(() => {
        notification.close();
      }, 10000);
    }
  };

  

  // Page visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('Page is now in background');
      } else {
        console.log('Page is now in foreground');
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
        // Immediately fetch tasks when page becomes visible
        fetchTasks();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Load theme preference for the current user
  useEffect(() => {
    if (user) {
      const savedTheme = localStorage.getItem(`theme_${user.id}`);
      if (savedTheme === 'dark') {
        setIsDarkMode(true);
        document.body.classList.add('dark-mode');
      } else {
        setIsDarkMode(false);
        document.body.classList.remove('dark-mode');
      }
    }
  }, [user]);

  // Load notifications from localStorage
  useEffect(() => {
    if (user) {
      const savedNotifications = localStorage.getItem(`notifications_${user.id}`);
      if (savedNotifications) {
        const parsedNotifications = JSON.parse(savedNotifications);
        setNotifications(parsedNotifications);
        
        parsedNotifications.forEach(notification => {
          if (notification.taskId) {
            processedTaskIds.current.add(notification.taskId);
          }
        });
      }
    }
  }, [user]);

  // Save notifications to localStorage
  const saveNotifications = (newNotifications) => {
    if (user) {
      localStorage.setItem(`notifications_${user.id}`, JSON.stringify(newNotifications));
    }
  };

  // 🔧 FIX: Enhanced real-time task checking with duplicate call prevention
  const checkForTaskUpdates = async () => {
    if (!user || isCheckingUpdates || isFetchingTasks) {
      console.log('Skipping task update check - already in progress or no user');
      return;
    }

    try {
      setIsCheckingUpdates(true);
      const response = await taskAPI.getTasks();
      const allTasks = response.data;
      
      // Update task count
      lastTaskCount.current = allTasks.length;

      // Check for new assignments (existing logic)
      const myTasks = allTasks.filter(task =>
        task.assignees?.some(a => String(a.id) === String(user.id))
      );

      const newAssignments = myTasks.filter(task => {
        const taskCreatedAt = new Date(task.created_at);
        const isNewTask = taskCreatedAt.getTime() > lastNotificationCheck.current;
        const notAlreadyProcessed = !processedTaskIds.current.has(task.id);

         // and the task was not created by the current user
        //const isAssignedToCurrentUser = task.assignees?.some(a => String(a.id) === String(user.id));
        const notCreatedByCurrentUser = !task.creator || String(task.creator.id) !== String(user.id);
        
        return isNewTask && notAlreadyProcessed && notCreatedByCurrentUser;
      });

      if (newAssignments.length > 0) {
        const newNotifications = newAssignments.map(task => {
          processedTaskIds.current.add(task.id);
          
          showNotificationWithSound(
            'New Task Assignment',
            `You have been assigned to: "${task.title}"`,
            task.id
          );
          
          return {
            id: `task_${task.id}_${Date.now()}_${Math.random()}`,
            type: 'task_assigned',
            title: 'New Task Assignment',
            message: `You have been assigned to task: "${task.title}"`,
            taskId: task.id,
            taskTitle: task.title,
            createdBy: task.creator?.name || task.creator?.username || 'Unknown',
            timestamp: new Date().toISOString(),
            read: false
          };
        });

        setNotifications(prev => {
          const updated = [...newNotifications, ...prev].slice(0, 20);
          saveNotifications(updated);
          return updated;
        });
      }

      // Update tasks in state for real-time display
      setTasks(prevTasks => {
  // For tasks that are recently updated, keep the local version
  return allTasks.map(task => {
    if (recentlyUpdatedTaskIds.includes(task.id)) {
      // Find the local version
      const local = prevTasks.find(t => t.id === task.id);
      return local ? local : task;
    }
    return task;
  });
});
      
      lastNotificationCheck.current = Date.now();
    } catch (error) {
      console.error('Error checking for task updates:', error);
    } finally {
      setIsCheckingUpdates(false);
    }
  };

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          console.log('Notification permission granted');
        } else {
          console.log('Notification permission denied');
        }
      });
    }
  }, []);

  // Toggle theme function
  const toggleTheme = () => {
    if (user) {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      if (newTheme) {
        document.body.classList.add('dark-mode');
        localStorage.setItem(`theme_${user.id}`, 'dark');
      } else {
        document.body.classList.remove('dark-mode');
        localStorage.setItem(`theme_${user.id}`, 'light');
      }
    }
  };

  // Update date and time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 🔧 FIX: Optimized real-time updates with reduced polling frequency
  useEffect(() => {
    if (user) {
      // Initial check and set task count
      checkForTaskUpdates();
      
      // Clear any existing interval
      if (taskUpdateInterval.current) {
        clearInterval(taskUpdateInterval.current);
      }
      
      // 🔧 FIX: Reduced frequency from 3 seconds to 30 seconds to prevent spam
      taskUpdateInterval.current = setInterval(() => {
        checkForTaskUpdates();
      }, 30000); // Check every 30 seconds - much more reasonable
      
      return () => {
        if (taskUpdateInterval.current) {
          clearInterval(taskUpdateInterval.current);
        }
      };
    }
  }, [user, isCheckingUpdates, isFetchingTasks]); // Added dependencies to prevent duplicate setups

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (taskUpdateInterval.current) {
        clearInterval(taskUpdateInterval.current);
      }
    };
  }, []);

  const formatDate = (date) => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notificationTime) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const markNotificationAsRead = (notificationId) => {
    setNotifications(prev => {
      const updated = prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      );
      saveNotifications(updated);
      return updated;
    });
  };

  const markAllAsRead = () => {
    setNotifications(prev => {
      const updated = prev.map(notification => ({ ...notification, read: true }));
      saveNotifications(updated);
      return updated;
    });
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    saveNotifications([]);
    processedTaskIds.current.clear();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const sortTasksByStatus = (tasksToSort) => {
    return tasksToSort.sort((a, b) => {
      const statusA = a.status || '';
      const statusB = b.status || '';
      
      const indexA = statusOrder.indexOf(statusA);
      const indexB = statusOrder.indexOf(statusB);
      
      const orderA = indexA === -1 ? statusOrder.length : indexA;
      const orderB = indexB === -1 ? statusOrder.length : indexB;
      
      return orderA - orderB;
    });
  };
// ...existing code...
const archivedStatuses = [
  "approved",
  "completed",
  "posted"
];
// ...existing code...
  // ...existing code...
const getFilteredTasks = (includeArchived = false) => {
  if (!user) return tasks;

  let filteredTasks;
  switch (activeTab) {
    case 'my':
      filteredTasks = tasks.filter(task =>
        task.assignees?.some(a => String(a.id) === String(user.id))
      );
      // Exclude archived unless explicitly included
      if (!includeArchived) {
        filteredTasks = filteredTasks.filter(
          task => !archivedStatuses.includes((task.status || '').toLowerCase())
        );
      }
      return sortTasksByStatus(filteredTasks);
    case 'created':
      filteredTasks = tasks.filter(task =>
        task.creator && String(task.creator.id) === String(user.id)
      );
      // Show all for created
      return filteredTasks;
    default:
      filteredTasks = tasks;
      if (!includeArchived) {
        filteredTasks = filteredTasks.filter(
          task => !archivedStatuses.includes((task.status || '').toLowerCase())
        );
      }
      return filteredTasks;
  }
};
// ...existing code...

  const getPendingTasksCount = () => {
  if (!user) return 0;
  
  const myTasks = tasks.filter(task =>
    task.assignees?.some(a => String(a.id) === String(user.id))
  );
  
  // Define pending statuses (tasks that are not completed, posted, or approved)
  const pendingStatuses = [
    "assigned",
    "not started", 
    "in progress", 
    "team review", 
  ];
  
  return myTasks.filter(task => 
    pendingStatuses.includes(task.status?.toLowerCase())
  ).length;
};

  // 🔧 FIX: Optimized fetchTasks with duplicate call prevention
  const fetchTasks = async () => {
    if (isFetchingTasks || isCheckingUpdates) {
      console.log('Skipping fetchTasks - already in progress');
      return;
    }

    try {
      setIsFetchingTasks(true);
      setError('');
      const response = await taskAPI.getTasks();
      setTasks(response.data);
      
      // Update task count for real-time detection
      lastTaskCount.current = response.data.length;
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      setError('Failed to load tasks');
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setIsFetchingTasks(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authAPI.getCurrentUser();
      setUserInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch user info:', err);
      setError('Failed to load user information');
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize user data on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      onLogout();
      return;
    }
    fetchCurrentUser();
  }, [onLogout]);

  // Initial fetch tasks when user is loaded
  useEffect(() => {
    if (user) {
      fetchTasks();
    }
  }, [user]);

  // 🔧 FIX: Optimized handleDeleteTask - state update only, no refetch needed
  const handleDeleteTask = async (taskId) => {
    try {
      await taskAPI.deleteTask(taskId);
      // 🔧 FIX: Update state directly instead of refetching all tasks
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      pausePolling();
      console.log('Task deleted successfully - state updated directly');
    } catch (err) {
      console.error('Failed to delete task:', err);
      alert("Failed to delete task. Please try again.");
    }
  };

  // 🔧 FIX: Optimized updateTask - state update only, no refetch needed
  const updateTask = async (taskId, updatedData) => {
  try {
    const response = await taskAPI.updateTask(taskId, updatedData);
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId ? { ...task, ...response.data } : task
      )
    );
    setRecentlyUpdatedTaskIds(ids => [...ids, taskId]);
    // Always fetch latest tasks from backend to ensure sync
    await fetchTasks();
    // Remove lock after 3 seconds
    setTimeout(() => {
      setRecentlyUpdatedTaskIds(ids => ids.filter(id => id !== taskId));
    }, 3000);
    pausePolling();
    console.log('Task updated and tasks re-fetched from backend');
    return response.data;
  } catch (err) {
    console.error('Failed to update task:', err.response?.data || err.message);
    if (err.response?.status === 401) {
      onLogout();
    }
    throw err;
  }
};

// ...existing code...

// Pause polling for a short time after a mutation to avoid race conditions
const pausePolling = (ms = 3000) => {
  if (taskUpdateInterval.current) {
    clearInterval(taskUpdateInterval.current);
    taskUpdateInterval.current = null;
  }
  setTimeout(() => {
    if (!taskUpdateInterval.current) {
      taskUpdateInterval.current = setInterval(() => {
        checkForTaskUpdates();
      }, 30000);
    }
  }, ms);
};
// ...existing code...


  // 🔧 FIX: Optimized task creation handler - single API call only
  const handleTaskCreated = async () => {
    console.log('Task created, refreshing task list...');
    
    // 🔧 FIX: Only call fetchTasks, remove duplicate checkForTaskUpdates
    await fetchTasks();
    pausePolling();
    // Show success notification
    if (Notification.permission === 'granted') {
      new Notification('Task Created Successfully', {
        body: 'Your new task has been created and is now visible.',
        icon: '/favicon.ico'
      });
    }
    
    // 🔧 FIX: Removed duplicate checkForTaskUpdates call
    // The interval will handle updates automatically
  };

  const handleUserAdd = (newUser) => {
    console.log('New user added:', newUser);
    setActiveTab('users');
  };

  const renderContent = () => {
    if (loading) {
  return (
    <div className="loading-container eq-loader" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px'
    }}>
      <div className="eq-bar eq-bar1"></div>
      <div className="eq-bar eq-bar2"></div>
      <div className="eq-bar eq-bar3"></div>
    </div>
  );
}

    if (error) {
      return (
        <div className="error-container" style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '16px',
          borderRadius: '8px',
          margin: '16px 0'
        }}>
          {error}
        </div>
      );
    }

    switch (activeTab) {
      case 'create':
        return <TaskCreate onTaskCreated={handleTaskCreated} />;

      case 'files':
        return (
          <>
            <h2>File Management</h2>
            <FileUpload />
          </>
        );
      
      case 'users':
        if (user?.role !== 'admin') {
          return (
            <div className="access-denied" style={{
              textAlign: 'center',
              padding: '40px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Access Denied</h2>
              <p style={{ color: '#6b7280' }}>You don't have permission to view this section.</p>
            </div>
          );
        }
        return <UserList />;

        case 'chat':
  return <Chat user={user} />;
      
      case 'add-user':
        if (user?.role !== 'admin') {
          return (
            <div className="access-denied" style={{
              textAlign: 'center',
              padding: '40px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Access Denied</h2>
              <p style={{ color: '#6b7280' }}>You don't have permission to add users.</p>
            </div>
          );
        }
        return (
          <div className="add-user-container">
            <AddUser 
              isOpen={true}
              onClose={() => setActiveTab('users')}
              onUserAdd={handleUserAdd}
            />
          </div>
        );
      
        case 'text_speech':
  return <TextToSpeech />;

  case 'text_translate':
  return <TextTranslate />;

  case 'tic_tac_toe':
    return <TicTacToe />;

  case 'sudoku':
    return <Sudoku />;

  case 'vtos':
    return <VtoS />;


      case 'user-data':
        if (user?.role !== 'admin') {
          return (
            <div className="access-denied" style={{
              textAlign: 'center',
              padding: '40px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Access Denied</h2>
              <p style={{ color: '#6b7280' }}>You don't have permission to view user data.</p>
            </div>
          );
        }
        return <UserData />;
      
      // ...existing code...
default:
  return (
    <>
      {activeTab === 'all' && (
        <>
          <div className="task-header-section">
            <h2 style={{fontSize: '1.5rem', fontWeight: 600, margin: 0}}>All Tasks</h2>
            <button 
              className="add-new-task-btn"
              onClick={() => setActiveTab('create')}
              title="Create New Task"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
                <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Add New Task
            </button>
          </div>
          <div style={{ margin: '16px 0' }}>
            <label style={{ cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={e => setShowArchived(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Show Archived Tasks (Approved, Completed, Posted)
            </label>
          </div>
        </>
      )}
      {activeTab === 'my' && (
        <>
          <div className="task-header-section">
            <div className="task-title-with-count">
              <h2 style={{fontSize: '1.5rem', fontWeight: 600, margin: 0}}>My Assigned Tasks</h2>
              <span className="pending-count">
                YOU HAVE {getPendingTasksCount()} PENDING TASK{getPendingTasksCount() !== 1 ? 'S' : ''}
              </span>
            </div>
            <button 
              className="add-new-task-btn"
              onClick={() => setActiveTab('create')}
              title="Create New Task"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
                <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Add New Task
            </button>
          </div>
          <div style={{ margin: '16px 0' }}>
            <label style={{ cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={e => setShowArchived(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Show Archived Tasks (Approved, Completed, Posted)
            </label>
          </div>
        </>
      )}
      {activeTab === 'created' && (
        <>
          <div className="task-header-section">
            <h2 style={{fontSize: '1.5rem', fontWeight: 600, margin: 0}}>Tasks Created By Me</h2>
            <button 
              className="add-new-task-btn"
              onClick={() => setActiveTab('create')}
              title="Create New Task"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
                <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Add New Task
            </button>
          </div>
        </>
      )}
      <TaskList
        tasks={getFilteredTasks(showArchived)}
        onUpdate={updateTask}
        onDelete={handleDeleteTask}
        user={user}
      />
    </>
  );
// ...existing code...
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar
        onLogout={onLogout}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        userRole={user?.role}
        isSidebarOpen={isSidebarOpen}           // add this
        setIsSidebarOpen={setIsSidebarOpen}   
      />

      <div className="main-content">
        <div className="dashboard-header">
          <button
  className="sidebar-toggle-btn"
  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
  title="Toggle Sidebar"
>
  ☰
</button>

          <div className="datetime-section">
            <div className="date-display">
              {formatDate(currentDateTime)}
            </div>
          </div>

          
          {user && (
            <div className="user-info">
              {/* Notification Bell */}
              <div className="notification-container">
                <button 
                  className="notification-bell"
                  onClick={() => setShowNotifications(!showNotifications)}
                  title="Notifications"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="notifications-dropdown">
                    <div className="notifications-header">
                      <h3>Notifications</h3>
                      <div className="notification-actions">
                        {unreadCount > 0 && (
                          <button onClick={markAllAsRead} className="mark-all-read">
                            Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button onClick={clearAllNotifications} className="clear-all">
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="notifications-list">
                      {notifications.length === 0 ? (
                        <div className="no-notifications">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <p>No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div 
                            key={notification.id} 
                            className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                            onClick={() => markNotificationAsRead(notification.id)}
                          >
                            <div className="notification-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                            </div>
                            <div className="notification-content">
                              <div className="notification-title">{notification.title}</div>
                              <div className="notification-message">{notification.message}</div>
                              <div className="notification-meta">
                                <span className="notification-creator">By {notification.createdBy}</span>
                                <span className="notification-time">{formatNotificationTime(notification.timestamp)}</span>
                              </div>
                            </div>
                            {!notification.read && <div className="unread-dot"></div>}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="user-details">
                <span className="user-name">{user.name || user.username}</span>
                <span className="designation">({user.role || 'User'})</span>
              </div>
              <button 
                className="theme-toggle-btn" 
                onClick={toggleTheme}
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDarkMode ? (
                  // Sun icon for light mode
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="1" x2="12" y2="3" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="21" x2="12" y2="23" stroke="currentColor" strokeWidth="2"/>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="currentColor" strokeWidth="2"/>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" stroke="currentColor" strokeWidth="2"/>
                    <line x1="1" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2"/>
                    <line x1="21" y1="12" x2="23" y2="12" stroke="currentColor" strokeWidth="2"/>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="currentColor" strokeWidth="2"/>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                ) : (
                  // Moon icon for dark mode
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" fill="currentColor"/>
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>

        <div style={{ padding: '24px' }}>
          {renderContent()}
        </div>
      </div>

      {/* Click outside to close notifications */}
      {showNotifications && (
        <div 
          className="notifications-overlay" 
          onClick={() => setShowNotifications(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;